/**
 * Bulk Action Bar
 *
 * Floating action bar that appears when items are selected
 * Provides bulk operations like delete, archive, export
 */
import { TrashIcon, ArchiveBoxIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Button from './Button'

interface BulkActionBarProps {
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onDeselectAll: () => void
  onDelete?: () => void
  onArchive?: () => void
  onActivate?: () => void
  onExport?: () => void
  actions?: {
    label: string
    icon?: React.ReactNode
    onClick: () => void
    variant?: 'primary' | 'secondary' | 'danger'
  }[]
}

export default function BulkActionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onDelete,
  onArchive,
  onActivate,
  onExport,
  actions,
}: BulkActionBarProps) {
  if (selectedCount === 0) return null

  const allSelected = selectedCount === totalCount

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
      <div className="bg-card rounded-lg shadow-2xl border border-border px-6 py-4 flex items-center gap-4">
        {/* Selection Info */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary-500 text-white rounded flex items-center justify-center text-sm font-semibold">
              {selectedCount}
            </div>
            <span className="text-sm font-medium text-foreground">
              {selectedCount === 1 ? 'item selected' : 'items selected'}
            </span>
          </div>

          {!allSelected && (
            <button
              onClick={onSelectAll}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Select all {totalCount}
            </button>
          )}
        </div>

        <div className="h-6 w-px bg-border" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Custom Actions */}
          {actions?.map((action, index) => (
            <Button
              key={index}
              onClick={action.onClick}
              variant={action.variant || 'secondary'}
              size="sm"
            >
              {action.icon && <span className="mr-1">{action.icon}</span>}
              {action.label}
            </Button>
          ))}

          {/* Standard Actions */}
          {onActivate && (
            <Button
              onClick={onActivate}
              variant="secondary"
              size="sm"
            >
              Activate
            </Button>
          )}

          {onArchive && (
            <Button
              onClick={onArchive}
              variant="secondary"
              size="sm"
            >
              <ArchiveBoxIcon className="w-4 h-4 mr-1" />
              Archive
            </Button>
          )}

          {onExport && (
            <Button
              onClick={onExport}
              variant="secondary"
              size="sm"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              Export
            </Button>
          )}

          {onDelete && (
            <Button
              onClick={onDelete}
              variant="secondary"
              size="sm"
              className="text-red-600 hover:text-red-600 hover:bg-red-500/10"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Delete
            </Button>
          )}
        </div>

        <div className="h-6 w-px bg-border" />

        {/* Clear Selection */}
        <button
          onClick={onDeselectAll}
          className="text-muted-foreground hover:text-muted-foreground transition-colors"
          title="Clear selection"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
